import {format} from 'date-fns';
import {chunk} from 'lodash-es';
import {useMemo} from 'react';

import afriexLogoDark from '@/assets/svg/afriex-new-logo.png';
import afriexLogoWhite from '@/assets/svg/afriex-new-logo-white.png';
import {currencyToCountry, euroCountries} from '@/constants/countries';
import currencies from '@/constants/currencies';
import {useCountryList} from '@/context/CountryListContext';
import {useDarkMode} from '@/context/ThemeProviderContext';
import type {AdminAfriexPerson, SupportedAssets} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';

// Balances are chunked into rows of at most 3, so a row splits the width evenly
// between its members. Any other size (i.e. a single balance) takes the row.
const balanceCellWidthByGroupSize: Record<number, string> = {
  3: 'w-1/3',
  2: 'w-1/2',
};

const StatementHeader = ({
  balances,
  dates,
  user,
}: {
  user: AdminAfriexPerson;
  balances: Partial<Record<SupportedAssets, number>>;
  dates: Date[];
}) => {
  const {isDark} = useDarkMode();
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const startDate = format(dates?.[0], 'MMMM d, yyyy');
  const endDate = format(dates?.[1], 'MMMM d, yyyy');
  const currentCountryCode = user.currentCountry?.toLocaleUpperCase() || 'US'; // Default to US if not available
  const currentCurrency = euroCountries.includes(currentCountryCode)
    ? 'EUR'
    : ((Object.keys(currencyToCountry).find(
        (key) => currencyToCountry[key] === currentCountryCode,
      ) || 'USD') as SupportedAssets);
  const flag = mapCountryCodeToFlags(countryList)[currentCountryCode];
  const currentBalance = balances[currentCurrency];
  const otherBalances = Object.entries(balances).filter(
    ([currency]) => currency !== currentCurrency,
  );

  // Create a map of currency codes to their full names
  const currencyNameMap = useMemo(() => {
    return currencies.reduce(
      (acc, curr) => {
        acc[curr.code] = curr.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, []);

  return (
    <div className="statement-header">
      <div className="flex justify-between items-center mb-3">
        <div className="logoCon items-center flex gap-1 w-50 mb-3">
          <div id="logoPng" className="logoSvg">
            <img
              src={isDark ? afriexLogoWhite : afriexLogoDark}
              alt="Afriex Logo"
            />
          </div>
        </div>

        <div className="text-right">
          <p className="text-sm font-semibold">Account Statement</p>
          <p className="text-xs text-cyan-700">
            {startDate} - {endDate}
          </p>
        </div>
      </div>

      {/* Account Holder */}
      <div>
        <p className="text-sm dark:text-gray-400">Account Holder</p>
        <p className="text-sm font-semibold">{user.name?.fullName}</p>
      </div>

      {/* Wallet balances */}
      <div className="flex flex-col mt-5">
        {/* top balance section */}
        <div className="flex items-center bg-receipt-bg dark:bg-dark-blueBg rounded-md mb-3">
          <div className="flex items-center gap-2 border-r-2 py-3 pl-4 w-1/3 dark:border-dark-borderMid">
            <div className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden">
              <span className="text-2xl">{flag}</span>
            </div>

            <div>
              <p className="text-xs">{currentCurrency} balance</p>
              <p className="text-xs font-medium">
                {currentBalance?.toLocaleString() || 0} {currentCurrency}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 border-r-2 dark:border-dark-borderMid py-3 px-4 w-1/3">
            <div>
              <p className="text-xs">Money in</p>
              <p className="text-xs font-medium">
                {user?.stats?.transactions?.DEPOSIT?.volumeAllTime?.toLocaleString() ||
                  0}{' '}
                {currentCurrency}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 py-3 px-4 w-1/3">
            <div>
              <p className="text-xs">Money out</p>
              <p className="text-xs font-medium">
                {user?.stats?.transactions?.WITHDRAW?.volumeAllTime?.toLocaleString() ||
                  0}{' '}
                {currentCurrency}
              </p>
            </div>
          </div>
        </div>

        {/* bottom balance section */}
        {otherBalances.some(([_, amount]) => amount > 0) && (
          <div
            className={`flex flex-col items-center bg-receipt-bg dark:bg-dark-blueBg rounded-md ${otherBalances?.length > 1 ? '' : 'w-1/3'}`}
          >
            {chunk(
              otherBalances.filter(([_, amount]) => amount > 0),
              3,
            ).map((balanceGroup, groupIndex, groups) => (
              <div
                key={groupIndex}
                className={`flex w-full ${groupIndex !== groups.length - 1 ? 'border-b-2 dark:border-dark-borderMid' : ''}`}
              >
                {balanceGroup.map(([currency, amount], idx) => {
                  const currencyName = currencyNameMap[currency] || currency;
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 py-3 px-4 ${
                        balanceCellWidthByGroupSize[balanceGroup.length] ??
                        'w-full'
                      } ${idx !== balanceGroup.length - 1 ? 'border-r-2 dark:border-dark-borderMid' : ''}`}
                    >
                      <div className="flex flex-col gap-1">
                        <p className="text-xs">{`${currencyName} balance`}</p>
                        <p className="text-xs font-medium">
                          {amount.toLocaleString()} {currency}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatementHeader;
